import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/store';

import { useFeatureGate } from '../../../../hooks/useFeatureGate';
import { ROUTE } from '../../../../routes';
import { getTenantSourceStateShared, type TenantSourceResult } from '../../../creatives/creatives-tenant-source';
import { resolveSiteUrl } from '../../../creatives/live-preview';
import { snapshotFromState, type OrderedSourceSnapshot } from '../../../creatives/ordered-source';
import { getOwnerGameLists, type OwnerGameList } from '../../../creatives/owner-game-lists-api';
import { OWN_LISTS_FLAG } from '../../../creatives/own-lists-gate';
import { getPageSectionsShared, type PageSectionsState } from '../../../creatives/page-arrangement-api';
import { useBrandSiteHost } from '../../../creatives/site-host';
import { HOME_PAGE } from '../../../creatives/site-games';
import { getSitePagesShared } from '../../../creatives/site-pages-api';
import { useGameSources } from '../../../creatives/useGameSources';
import { resolveSummary, unaddressableView, type WebsiteCardView } from '../../card-summary';
import { useBrandSettled } from '../../useBrandSettled';
import type { ReadStatus } from '../creatives/creatives-summary';
import { gamesCheck, platformListIds } from './games-check';
import { gamesCopy } from './games-copy';
import { deriveGamesSummary } from './games-summary';

/** What the Homepage reads returned. */
type Homepage = {
  /** The Homepage's arrangement; null when the owner cannot arrange it (its source does not allow the brand's
   *  override, as the Games tab gates it), which the service refuses to read. */
  sections: PageSectionsState | null;
  /** The owner's own lists on the Homepage; none read with `owner-game-lists` off. */
  ownLists: OwnerGameList[];
  /** Each platform list's saved state, for the lists the service answered for. */
  snapshots: Record<string, OrderedSourceSnapshot>;
};

/** What the reads returned, stamped with the brand, host and flag they were made for. */
type Reads = { key: string } & ({ status: 'failed' } | ({ status: 'ready' } & Homepage));

const readsKey = (casinoInstanceId: string, host: string, ownListsOn: boolean): string =>
  `${casinoInstanceId}|${host}|${ownListsOn ? 'own' : 'platform'}`;

/** The site's host as the Games tab sends it to the service: the preview host made a site URL, then its host. */
const siteHostOf = (previewDomain: string | undefined): string => {
  const url = resolveSiteUrl(previewDomain);
  if (!url) return '';
  try {
    return new URL(url).host;
  } catch {
    return '';
  }
};

const NO_OWN_LISTS: TenantSourceResult<OwnerGameList[]> = { ok: true, value: [] };
const NOT_ARRANGEABLE: TenantSourceResult<null> = { ok: true, value: null };

/**
 * The Homepage's state, read only: the site's pages (for the Homepage's source), then its arrangement and, with the
 * flag on, the owner's lists, then each platform list's saved state. A Homepage the owner cannot arrange has no
 * arrangement to read (the service refuses it), and so no platform list to name: its owner's lists are all that is
 * read. A Homepage the service does not name as a page of sections, or a failed or unavailable read of the pages, the
 * arrangement or the owner's lists, is a failed card; a platform list whose state could not be read is left out (it
 * shows nothing to publish). The site's pages, the arrangement and the lists' states are the brand's shared reads
 * (`getSitePagesShared`, `getPageSectionsShared`, `getTenantSourceStateShared`), read without `signal`: a card left
 * while they are out never cancels them for another reader (Game Lists opened meanwhile, or the card mounted again),
 * who joins them or opens on their answer. The checks after them drop a superseded read's answer here. Only the
 * owner's lists are read under `signal`.
 */
const readHomepage = async (
  casinoInstanceId: string,
  host: string,
  ownListsOn: boolean,
  signal: AbortSignal,
): Promise<{ status: 'failed' } | ({ status: 'ready' } & Homepage)> => {
  const pages = await getSitePagesShared(casinoInstanceId, host);
  const home = pages.ok ? pages.value.pages.find((page) => page.path === HOME_PAGE) : undefined;
  if (!home || home.kind !== 'sections' || home.source_id === '' || signal.aborted) return { status: 'failed' };

  const [sections, own] = await Promise.all([
    home.overrides_allowed ? getPageSectionsShared(casinoInstanceId, home.source_id) : Promise.resolve(NOT_ARRANGEABLE),
    ownListsOn ? getOwnerGameLists(casinoInstanceId, HOME_PAGE, signal) : Promise.resolve(NO_OWN_LISTS),
  ]);
  if (!sections.ok || !own.ok || signal.aborted) return { status: 'failed' };

  const listIds = sections.value ? platformListIds(sections.value) : [];
  const states = await Promise.all(
    listIds.map((listId) => getTenantSourceStateShared(casinoInstanceId, { kind: 'game_list', source_id: listId })),
  );
  const snapshots: Record<string, OrderedSourceSnapshot> = {};
  states.forEach((state, i) => {
    if (state.ok) snapshots[listIds[i]] = snapshotFromState(state.value);
  });
  return {
    status: 'ready',
    sections: sections.value,
    ownLists: own.value,
    snapshots,
  };
};

/**
 * The Website Game Lists card: whether the Homepage has anything to publish, by the Games tab's own rules, else how
 * many lists it has. Read only, and without the live site: the same service reads the tab opens with (the site's
 * pages, the Homepage's arrangement, the owner's lists, each platform list's saved state), by the host the tab sends.
 * On a Homepage the owner cannot arrange, the owner's lists alone, and no count: unless one of them waits, no line.
 * The several-megabyte game catalogue is read only when nothing else is a change and an automatic list's saved order
 * differs while its filters do not (see `games-check.ts`); the card stays loading until it is in, and fails if its read
 * does. Re-reads on a brand switch or a flag flip; the answers of a superseded brand's reads are dropped, and its read
 * of the owner's lists is aborted: the others are shared with the brand's other readers (`readHomepage`).
 */
export const useGamesCard = (): WebsiteCardView => {
  const { t } = useTranslation();
  const displayBrand = useAppSelector((state) => state.casinoInstance.displayBrand);
  const brandSettled = useBrandSettled();
  const tenantRaw = Number(displayBrand?.tenant_id);
  const tenantId = Number.isFinite(tenantRaw) && tenantRaw > 0 ? tenantRaw : undefined;
  const casinoInstanceId = displayBrand?.id || undefined;
  const brandDomain = displayBrand?.casino_data?.details?.domain;
  const ownListsOn = useFeatureGate().isEnabled(OWN_LISTS_FLAG);
  const { previewDomain, resolving } = useBrandSiteHost(tenantId, brandDomain);
  const host = siteHostOf(previewDomain);

  const [reads, setReads] = React.useState<Reads | null>(null);

  React.useEffect(() => {
    if (casinoInstanceId === undefined || resolving || host === '') return undefined;
    const key = readsKey(casinoInstanceId, host, ownListsOn);
    const controller = new AbortController();
    const { signal } = controller;
    void (async () => {
      const next = await readHomepage(casinoInstanceId, host, ownListsOn, signal);
      if (!signal.aborted) setReads({ key, ...next });
    })();
    return () => controller.abort();
  }, [casinoInstanceId, host, resolving, ownListsOn]);

  // Until this brand's reads land, the card shows them as loading, never the previous brand's.
  const current =
    casinoInstanceId !== undefined && reads?.key === readsKey(casinoInstanceId, host, ownListsOn) ? reads : null;
  const firstCheck = React.useMemo(
    () =>
      current?.status === 'ready'
        ? gamesCheck({
            ownListsOn,
            sections: current.sections,
            ownLists: current.ownLists,
            snapshots: current.snapshots,
            catalogue: null,
          })
        : null,
    [current, ownListsOn],
  );
  const needsCatalogue = firstCheck?.verdict === 'needs-catalogue';
  const { catalog, loading: catalogueLoading, failed: catalogueReadFailed } = useGameSources('all', needsCatalogue);
  // A catalogue whose read failed (or settled with nothing in it) resolves no automatic list on either side, which
  // would read as clean: the card cannot tell then, and fails as it does for the other reads.
  const catalogueFailed = needsCatalogue && (catalogueReadFailed || (!catalogueLoading && catalog.length === 0));
  const check = React.useMemo(() => {
    if (!needsCatalogue || current?.status !== 'ready') return firstCheck;
    if (catalogueLoading || catalogueFailed) return null;
    return gamesCheck({
      ownListsOn,
      sections: current.sections,
      ownLists: current.ownLists,
      snapshots: current.snapshots,
      catalogue: catalog,
    });
  }, [needsCatalogue, current, firstCheck, catalogueLoading, catalogueFailed, catalog, ownListsOn]);

  if (tenantId === undefined || casinoInstanceId === undefined) {
    return unaddressableView(brandSettled, {
      label: gamesCopy(t, { id: 'open-game-lists' }),
      to: ROUTE.gameLists(),
    });
  }
  // No host once the lookup is over: the brand has no site the service could read the pages of.
  const status: ReadStatus =
    !resolving && host === ''
      ? 'failed'
      : current === null
        ? 'loading'
        : current.status === 'failed' || catalogueFailed
          ? 'failed'
          : check === null
            ? 'loading'
            : 'ready';
  return resolveSummary(
    deriveGamesSummary({
      status,
      changed: check?.verdict === 'changed',
      lists: check?.lists ?? null,
    }),
    (c) => gamesCopy(t, c),
    () => undefined,
  );
};
